// src/components/HqAcceptCompanyInvitation.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import api from "../axiosConfig";

import "../styles/ResetPassword.css";
import "../styles/HqAcceptCompanyInvitation.css";

const COMMON_WEAK_PASSWORDS = new Set([
  "123456",
  "12345678",
  "123456789",
  "password",
  "qwerty",
  "abc123",
  "111111",
  "123123",
  "admin",
  "admin123",
  "loadevo",
  "loadevo123",
]);

function getPasswordRules(
  password
) {
  const normalizedPassword =
    String(
      password ||
        ""
    );

  return {
    minLength:
      normalizedPassword.length >=
      10,

    maxLength:
      normalizedPassword.length <=
      128,

    hasLowercase:
      /[a-z]/.test(
        normalizedPassword
      ),

    hasUppercase:
      /[A-Z]/.test(
        normalizedPassword
      ),

    hasNumber:
      /\d/.test(
        normalizedPassword
      ),

    isNotCommon:
      normalizedPassword.length >
        0 &&
      !COMMON_WEAK_PASSWORDS.has(
        normalizedPassword.toLowerCase()
      ),
  };
}

function getPasswordValidationError(
  password
) {
  const rules =
    getPasswordRules(
      password
    );

  if (!rules.minLength) {
    return "A palavra-passe deve ter pelo menos 10 caracteres.";
  }

  if (!rules.maxLength) {
    return "A palavra-passe é demasiado longa.";
  }

  if (!rules.isNotCommon) {
    return "Esta palavra-passe é demasiado comum. Escolha uma palavra-passe mais segura.";
  }

  if (
    !rules.hasLowercase ||
    !rules.hasUppercase ||
    !rules.hasNumber
  ) {
    return "A palavra-passe deve incluir uma letra maiúscula, uma letra minúscula e um número.";
  }

  return "";
}

function getPasswordStrength(
  password
) {
  const value =
    String(
      password ||
        ""
    );

  if (!value) {
    return {
      label:
        "Ainda não definida",

      level:
        "empty",
    };
  }

  let score =
    0;

  if (
    value.length >=
    10
  ) {
    score +=
      1;
  }

  if (
    value.length >=
    14
  ) {
    score +=
      1;
  }

  if (
    /[a-z]/.test(
      value
    ) &&
    /[A-Z]/.test(
      value
    )
  ) {
    score +=
      1;
  }

  if (
    /\d/.test(
      value
    )
  ) {
    score +=
      1;
  }

  if (
    /[^A-Za-z0-9]/.test(
      value
    )
  ) {
    score +=
      1;
  }

  if (
    COMMON_WEAK_PASSWORDS.has(
      value.toLowerCase()
    )
  ) {
    score =
      0;
  }

  if (
    score <=
    2
  ) {
    return {
      label:
        "Fraca",

      level:
        "weak",
    };
  }

  if (
    score <=
    4
  ) {
    return {
      label:
        "Média",

      level:
        "medium",
    };
  }

  return {
    label:
      "Forte",

    level:
      "strong",
  };
}

function formatInvitationExpiration(
  value
) {
  if (!value) {
    return "";
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  try {
    return new Intl.DateTimeFormat(
      "pt-PT",
      {
        dateStyle:
          "long",

        timeStyle:
          "short",

        timeZone:
          "Europe/Lisbon",
      }
    ).format(
      date
    );
  } catch {
    return date.toLocaleString(
      "pt-PT"
    );
  }
}

function PasswordVisibilityIcon({
  visible,
}) {
  if (visible) {
    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M17.94 17.94A10.94 10.94 0 0112 20C7 20 2.73 16.11 1 12c.46-1.06 1.08-2.06 1.82-2.94M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88M7.11 7.11A10.94 10.94 0 0112 4c5 0 9.27 3.89 11 8-.6 1.4-1.5 2.7-2.6 3.82M3 3l18 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />

      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M12 3l7 3v5c0 4.8-2.9 8.3-7 10-4.1-1.7-7-5.2-7-10V6l7-3z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />

      <path
        d="M9 12l2 2 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M20 11.1V12a8 8 0 11-4.7-7.3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />

      <path
        d="M20 4l-9 9-3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RuleStatusIcon({
  valid,
}) {
  return (
    <span
      className={[
        "hq-invite-rule-icon",
        valid
          ? "is-valid"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
    >
      {valid
        ? "✓"
        : "•"}
    </span>
  );
}

function InvitationDetail({
  label,
  value,
  fullWidth = false,
}) {
  if (!value) {
    return null;
  }

  return (
    <div
      className={[
        "hq-invite-detail",
        fullWidth
          ? "hq-invite-detail--full"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="hq-invite-detail-label">
        {label}
      </span>

      <strong className="hq-invite-detail-value">
        {value}
      </strong>
    </div>
  );
}

export default function HqAcceptCompanyInvitation() {
  const [
    params,
  ] =
    useSearchParams();

  const navigate =
    useNavigate();

  const token =
    useMemo(
      () =>
        String(
          params.get(
            "token"
          ) ||
            ""
        ).trim(),
      [
        params,
      ]
    );

  const passwordRef =
    useRef(
      null
    );

  const [
    invitationData,
    setInvitationData,
  ] =
    useState(
      null
    );

  const [
    password,
    setPassword,
  ] =
    useState(
      ""
    );

  const [
    confirmPassword,
    setConfirmPassword,
  ] =
    useState(
      ""
    );

  const [
    tokenChecking,
    setTokenChecking,
  ] =
    useState(
      true
    );

  const [
    tokenValid,
    setTokenValid,
  ] =
    useState(
      false
    );

  const [
    busy,
    setBusy,
  ] =
    useState(
      false
    );

  const [
    done,
    setDone,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      ""
    );

  const [
    showPassword,
    setShowPassword,
  ] =
    useState(
      false
    );

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] =
    useState(
      false
    );

  const [
    capsOn,
    setCapsOn,
  ] =
    useState(
      false
    );

  const [
    invitationDetailsOpen,
    setInvitationDetailsOpen,
  ] =
    useState(
      false
    );

  const passwordRules =
    useMemo(
      () =>
        getPasswordRules(
          password
        ),
      [
        password,
      ]
    );

  const passwordStrength =
    useMemo(
      () =>
        getPasswordStrength(
          password
        ),
      [
        password,
      ]
    );

  const allPasswordRulesValid =
    passwordRules.minLength &&
    passwordRules.maxLength &&
    passwordRules.hasLowercase &&
    passwordRules.hasUppercase &&
    passwordRules.hasNumber &&
    passwordRules.isNotCommon;

  const passwordsMatch =
    confirmPassword.length >
      0 &&
    password ===
      confirmPassword;

  const confirmPasswordInvalid =
    confirmPassword.length >
      0 &&
    password !==
      confirmPassword;

  const canSubmit =
    tokenValid &&
    allPasswordRulesValid &&
    passwordsMatch &&
    !busy &&
    !done;

  useEffect(
    () => {
      if (
        tokenValid
      ) {
        passwordRef.current?.focus();
      }
    },
    [
      tokenValid,
    ]
  );

  useEffect(
    () => {
      const pageClassName =
        "hq-company-invitation-page-active";

      const rootElement =
        document.getElementById(
          "root"
        );

      document.documentElement.classList.add(
        pageClassName
      );

      document.body.classList.add(
        pageClassName
      );

      rootElement?.classList.add(
        pageClassName
      );

      return () => {
        document.documentElement.classList.remove(
          pageClassName
        );

        document.body.classList.remove(
          pageClassName
        );

        rootElement?.classList.remove(
          pageClassName
        );
      };
    },
    []
  );

  useEffect(
    () => {
      const rootElement =
        document.getElementById(
          "root"
        );

      const previousHtmlHeight =
        document.documentElement.style.height;

      const previousHtmlOverflowY =
        document.documentElement.style.overflowY;

      const previousBodyHeight =
        document.body.style.height;

      const previousBodyMinHeight =
        document.body.style.minHeight;

      const previousBodyOverflowY =
        document.body.style.overflowY;

      const previousRootHeight =
        rootElement?.style.height ||
        "";

      const previousRootMinHeight =
        rootElement?.style.minHeight ||
        "";

      const previousRootOverflow =
        rootElement?.style.overflow ||
        "";

      document.documentElement.style.height =
        "auto";

      document.documentElement.style.overflowY =
        "auto";

      document.body.style.height =
        "auto";

      document.body.style.minHeight =
        "100%";

      document.body.style.overflowY =
        "auto";

      if (
        rootElement
      ) {
        rootElement.style.height =
          "auto";

        rootElement.style.minHeight =
          "100%";

        rootElement.style.overflow =
          "visible";
      }

      return () => {
        document.documentElement.style.height =
          previousHtmlHeight;

        document.documentElement.style.overflowY =
          previousHtmlOverflowY;

        document.body.style.height =
          previousBodyHeight;

        document.body.style.minHeight =
          previousBodyMinHeight;

        document.body.style.overflowY =
          previousBodyOverflowY;

        if (
          rootElement
        ) {
          rootElement.style.height =
            previousRootHeight;

          rootElement.style.minHeight =
            previousRootMinHeight;

          rootElement.style.overflow =
            previousRootOverflow;
        }
      };
    },
    []
  );

  useEffect(
    () => {
      let cancelled =
        false;

      async function validateInvitation() {
        if (!token) {
          if (
            !cancelled
          ) {
            setError(
              "O link de ativação é inválido ou está incompleto."
            );

            setTokenValid(
              false
            );

            setTokenChecking(
              false
            );
          }

          return;
        }

        setTokenChecking(
          true
        );

        setError(
          ""
        );

        try {
          const response =
            await api.post(
              "/auth/company-invitations/validate",
              {
                token,
              }
            );

          if (
            cancelled
          ) {
            return;
          }

          setInvitationData(
            response.data ||
              null
          );

          setTokenValid(
            response.data?.valid ===
              true
          );
        } catch (requestError) {
          if (
            cancelled
          ) {
            return;
          }

          console.error(
            "[hq-company-invitation][validate] failed",
            requestError
          );

          setInvitationData(
            null
          );

          setTokenValid(
            false
          );

          setError(
            requestError?.response?.data?.error ||
              requestError?.response?.data?.message ||
              "Este convite é inválido, já expirou ou já foi utilizado."
          );
        } finally {
          if (
            !cancelled
          ) {
            setTokenChecking(
              false
            );
          }
        }
      }

      validateInvitation();

      return () => {
        cancelled =
          true;
      };
    },
    [
      token,
    ]
  );

  function handlePasswordKeyEvent(
    event
  ) {
    const capsLockActive =
      event.getModifierState?.(
        "CapsLock"
      );

    if (
      typeof capsLockActive ===
      "boolean"
    ) {
      setCapsOn(
        capsLockActive
      );
    }
  }

  function handlePasswordChange(
    event
  ) {
    setPassword(
      event.target.value
    );

    if (error) {
      setError(
        ""
      );
    }
  }

  function handleConfirmPasswordChange(
    event
  ) {
    setConfirmPassword(
      event.target.value
    );

    if (error) {
      setError(
        ""
      );
    }
  }

  async function handleSubmit(
    event
  ) {
    event.preventDefault();

    if (
      busy ||
      done
    ) {
      return;
    }

    setError(
      ""
    );

    if (!token) {
      setError(
        "O link de ativação é inválido ou está incompleto."
      );

      return;
    }

    if (!tokenValid) {
      setError(
        "Este convite é inválido, já expirou ou já foi utilizado."
      );

      return;
    }

    const passwordValidationError =
      getPasswordValidationError(
        password
      );

    if (
      passwordValidationError
    ) {
      setError(
        passwordValidationError
      );

      return;
    }

    if (
      password !==
      confirmPassword
    ) {
      setError(
        "As palavras-passe não coincidem."
      );

      return;
    }

    setBusy(
      true
    );

    try {
      const response =
        await api.post(
          "/auth/company-invitations/accept",
          {
            token,
            password,
          }
        );

      if (
        response.data?.activated !==
        true
      ) {
        throw new Error(
          "A ativação não foi confirmada pelo servidor."
        );
      }

      setDone(
        true
      );

      setTokenValid(
        false
      );

      setPassword(
        ""
      );

      setConfirmPassword(
        ""
      );

      window.setTimeout(
        () => {
          navigate(
            "/?accountActivated=1",
            {
              replace:
                true,
            }
          );
        },
        2600
      );
    } catch (requestError) {
      console.error(
        "[hq-company-invitation][accept] failed",
        requestError
      );

      const errorCode =
        requestError?.response?.data?.code ||
        "";

      if (
        [
          "HQ_COMPANY_INVITATION_INVALID",
          "HQ_COMPANY_INVITATION_EXPIRED",
          "HQ_COMPANY_INVITATION_CANCELLED",
          "HQ_COMPANY_INVITATION_ALREADY_ACCEPTED",
          "HQ_COMPANY_ALREADY_ACTIVE",
        ].includes(
          errorCode
        )
      ) {
        setTokenValid(
          false
        );
      }

      setError(
        requestError?.response?.data?.error ||
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Não foi possível ativar esta conta."
      );
    } finally {
      setBusy(
        false
      );
    }
  }

  const companyName =
    invitationData?.company?.name ||
    "";

  const userName =
    invitationData?.user?.name ||
    "";

  const username =
    invitationData?.user?.username ||
    "";

  const email =
    invitationData?.user?.email ||
    invitationData?.invitation?.email ||
    "";

  const expirationText =
    formatInvitationExpiration(
      invitationData?.invitation?.expiresAt
    );

  return (
    <div className="reset-shell hq-invite-shell">
      <div
        className="reset-bg"
        aria-hidden="true"
      >
        <div className="reset-bg-grid" />

        <div className="reset-bg-glow reset-bg-glow--a" />

        <div className="reset-bg-glow reset-bg-glow--b" />
      </div>

      <main className="reset-card hq-invite-card">
        <div className="hq-invite-topbar">
          <Link
            to="/"
            className="hq-invite-brand-link"
            aria-label="LOADEvo"
          >
            <img
              src="/loadevo-logo.png"
              alt="LOADEvo"
              className="hq-invite-logo"
            />
          </Link>

          <div className="hq-invite-secure-badge">
            <ShieldIcon />

            <span>
              Convite seguro e de uso único
            </span>
          </div>
        </div>

        {tokenChecking ? (
          <section
            className="hq-invite-state"
            role="status"
            aria-live="polite"
          >
            <div className="hq-invite-state-spinner" />

            <div className="hq-invite-state-label">
              A validar convite
            </div>

            <h1 className="hq-invite-state-title">
              Estamos a confirmar a segurança do link
            </h1>

            <p className="hq-invite-state-text">
              Este processo demora apenas alguns segundos.
            </p>
          </section>
        ) : done ? (
          <section
            className="hq-invite-state"
            role="status"
            aria-live="polite"
          >
            <div className="hq-invite-success-icon">
              <SuccessIcon />
            </div>

            <div className="hq-invite-state-label hq-invite-state-label--success">
              Conta ativada
            </div>

            <h1 className="hq-invite-state-title">
              A sua conta está pronta
            </h1>

            <p className="hq-invite-state-text">
              A empresa e a conta de administrador foram ativadas com sucesso.
            </p>

            <p className="hq-invite-state-note">
              Será encaminhado para o início de sessão.
            </p>

            <Link
              className="hq-invite-state-button"
              to="/?accountActivated=1"
            >
              Iniciar sessão
            </Link>
          </section>
        ) : tokenValid ? (
          <>
            <header className="hq-invite-hero">
              <div className="hq-invite-eyebrow">
                Configuração inicial
              </div>

              <h1 className="hq-invite-title">
                Ative a sua conta LOADEvo
              </h1>

              <p className="hq-invite-subtitle">
                Confirme os dados do convite e escolha uma palavra-passe segura para começar.
              </p>
            </header>

            <form
              className="hq-invite-layout"
              onSubmit={
                handleSubmit
              }
              onKeyUp={
                handlePasswordKeyEvent
              }
              onKeyDown={
                handlePasswordKeyEvent
              }
            >
              <aside
                className={[
                  "hq-invite-panel",
                  "hq-invite-panel--summary",
                  "hq-invite-panel--summary-collapsible",
                  invitationDetailsOpen
                    ? "is-open"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <button
                  type="button"
                  className="hq-invite-summary-toggle"
                  onClick={
                    () =>
                      setInvitationDetailsOpen(
                        (
                          currentValue
                        ) =>
                          !currentValue
                      )
                  }
                  aria-expanded={
                    invitationDetailsOpen
                  }
                  aria-controls="hq-invite-summary-content"
                >
                  <span className="hq-invite-summary-toggle-main">
                    <span className="hq-invite-summary-toggle-icon">
                      <ShieldIcon />
                    </span>

                    <span className="hq-invite-summary-toggle-copy">
                      <span className="hq-invite-panel-kicker">
                        Dados do convite
                      </span>

                      <strong className="hq-invite-summary-toggle-title">
                        {companyName
                          ? `Convite para ${companyName}`
                          : "Consultar dados do convite"}
                      </strong>

                      <span className="hq-invite-summary-toggle-description">
                        Empresa, administrador, utilizador e validade
                      </span>
                    </span>
                  </span>

                  <span
                    className={[
                      "hq-invite-summary-toggle-chevron",
                      invitationDetailsOpen
                        ? "is-open"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-hidden="true"
                  >
                    <svg
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M6 8l4 4 4-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>

                <div
                  id="hq-invite-summary-content"
                  className="hq-invite-summary-content"
                  hidden={
                    !invitationDetailsOpen
                  }
                >
                  <div className="hq-invite-details-grid">
                    <InvitationDetail
                      label="Empresa"
                      value={
                        companyName
                      }
                    />

                    <InvitationDetail
                      label="Administrador"
                      value={
                        userName
                      }
                    />

                    <InvitationDetail
                      label="Utilizador"
                      value={
                        username
                          ? `@${username}`
                          : ""
                      }
                    />

                    <InvitationDetail
                      label="Email"
                      value={
                        email
                      }
                    />
                  </div>

                  <div className="hq-invite-summary-meta">
                    {expirationText && (
                      <div className="hq-invite-expiry">
                        <div className="hq-invite-expiry-icon">
                          <ShieldIcon />
                        </div>

                        <div>
                          <div className="hq-invite-expiry-label">
                            Convite válido até
                          </div>

                          <strong className="hq-invite-expiry-value">
                            {expirationText}
                          </strong>
                        </div>
                      </div>
                    )}

                    <div className="hq-invite-security-note">
                      <ShieldIcon />

                      <p>
                        Este link só pode ser utilizado uma vez. Nunca partilhe a palavra-passe criada nesta página.
                      </p>
                    </div>
                  </div>
                </div>
              </aside>

              <section className="hq-invite-panel hq-invite-panel--form">
                <div className="hq-invite-panel-header">
                  <div className="hq-invite-panel-kicker">
                    Segurança
                  </div>

                  <h2 className="hq-invite-panel-title">
                    Crie a sua palavra-passe
                  </h2>

                  <p className="hq-invite-panel-description">
                    Utilize uma combinação que não use noutros serviços.
                  </p>
                </div>

                {error && (
                  <div
                    className="hq-invite-alert"
                    role="alert"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 9v4m0 4h.01M10.29 3.86l-8.4 14.54A1.5 1.5 0 003.2 21h17.6a1.5 1.5 0 001.31-2.6L13.7 3.86a1.5 1.5 0 00-2.6 0z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <span>
                      {error}
                    </span>
                  </div>
                )}

                <label className="hq-invite-field">
                  <span className="hq-invite-field-label">
                    Palavra-passe
                  </span>

                  <div
                    className={[
                      "hq-invite-input-wrap",
                      password &&
                      allPasswordRulesValid
                        ? "is-valid"
                        : "",
                      error
                        ? "is-invalid"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <input
                      ref={
                        passwordRef
                      }
                      className="hq-invite-input"
                      type={
                        showPassword
                          ? "text"
                          : "password"
                      }
                      autoComplete="new-password"
                      value={
                        password
                      }
                      onChange={
                        handlePasswordChange
                      }
                      onKeyUp={
                        handlePasswordKeyEvent
                      }
                      onKeyDown={
                        handlePasswordKeyEvent
                      }
                      disabled={
                        busy
                      }
                      placeholder="Introduza uma palavra-passe"
                      required
                      minLength={
                        10
                      }
                      maxLength={
                        128
                      }
                      aria-invalid={
                        Boolean(
                          error
                        )
                      }
                    />

                    <button
                      type="button"
                      className="hq-invite-eye"
                      onClick={
                        () =>
                          setShowPassword(
                            (
                              currentValue
                            ) =>
                              !currentValue
                          )
                      }
                      aria-label={
                        showPassword
                          ? "Ocultar palavra-passe"
                          : "Mostrar palavra-passe"
                      }
                      disabled={
                        busy
                      }
                    >
                      <PasswordVisibilityIcon
                        visible={
                          showPassword
                        }
                      />
                    </button>
                  </div>
                </label>

                <label className="hq-invite-field">
                  <span className="hq-invite-field-label">
                    Confirmar palavra-passe
                  </span>

                  <div
                    className={[
                      "hq-invite-input-wrap",
                      passwordsMatch
                        ? "is-valid"
                        : "",
                      confirmPasswordInvalid
                        ? "is-invalid"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <input
                      className="hq-invite-input"
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      autoComplete="new-password"
                      value={
                        confirmPassword
                      }
                      onChange={
                        handleConfirmPasswordChange
                      }
                      onKeyUp={
                        handlePasswordKeyEvent
                      }
                      onKeyDown={
                        handlePasswordKeyEvent
                      }
                      disabled={
                        busy
                      }
                      placeholder="Repita a palavra-passe"
                      required
                      minLength={
                        10
                      }
                      maxLength={
                        128
                      }
                      aria-invalid={
                        confirmPasswordInvalid
                      }
                    />

                    <button
                      type="button"
                      className="hq-invite-eye"
                      onClick={
                        () =>
                          setShowConfirmPassword(
                            (
                              currentValue
                            ) =>
                              !currentValue
                          )
                      }
                      aria-label={
                        showConfirmPassword
                          ? "Ocultar palavra-passe"
                          : "Mostrar palavra-passe"
                      }
                      disabled={
                        busy
                      }
                    >
                      <PasswordVisibilityIcon
                        visible={
                          showConfirmPassword
                        }
                      />
                    </button>
                  </div>

                  {confirmPassword.length >
                    0 && (
                    <div
                      className={[
                        "hq-invite-match-status",
                        passwordsMatch
                          ? "is-valid"
                          : "is-invalid",
                      ].join(" ")}
                      aria-live="polite"
                    >
                      {passwordsMatch
                        ? "✓ As palavras-passe coincidem"
                        : "As palavras-passe ainda não coincidem"}
                    </div>
                  )}

                  {capsOn && (
                    <div className="hq-invite-caps-warning">
                      O Caps Lock está ativado
                    </div>
                  )}
                </label>

                <div className="hq-invite-password-status">
                  <div className="hq-invite-strength-header">
                    <span>
                      Segurança da palavra-passe
                    </span>

                    <strong
                      className={`hq-invite-strength-label hq-invite-strength-label--${passwordStrength.level}`}
                    >
                      {passwordStrength.label}
                    </strong>
                  </div>

                  <div
                    className={`hq-invite-strength-track hq-invite-strength-track--${passwordStrength.level}`}
                    aria-hidden="true"
                  >
                    <span />
                    <span />
                    <span />
                  </div>

                  <div className="hq-invite-rules-grid">
                    <div className="hq-invite-rule">
                      <RuleStatusIcon
                        valid={
                          passwordRules.minLength
                        }
                      />

                      <span>
                        Pelo menos 10 caracteres
                      </span>
                    </div>

                    <div className="hq-invite-rule">
                      <RuleStatusIcon
                        valid={
                          passwordRules.hasUppercase
                        }
                      />

                      <span>
                        Uma letra maiúscula
                      </span>
                    </div>

                    <div className="hq-invite-rule">
                      <RuleStatusIcon
                        valid={
                          passwordRules.hasLowercase
                        }
                      />

                      <span>
                        Uma letra minúscula
                      </span>
                    </div>

                    <div className="hq-invite-rule">
                      <RuleStatusIcon
                        valid={
                          passwordRules.hasNumber
                        }
                      />

                      <span>
                        Pelo menos um número
                      </span>
                    </div>

                    <div className="hq-invite-rule hq-invite-rule--full">
                      <RuleStatusIcon
                        valid={
                          passwordRules.isNotCommon
                        }
                      />

                      <span>
                        Não é uma palavra-passe comum
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="hq-invite-submit"
                  disabled={
                    !canSubmit
                  }
                >
                  {busy ? (
                    <>
                      <span
                        className="hq-invite-submit-spinner"
                        aria-hidden="true"
                      />

                      <span>
                        A ativar a conta…
                      </span>
                    </>
                  ) : (
                    <>
                      <span>
                        Ativar conta
                      </span>

                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 12h14m-5-5l5 5-5 5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </>
                  )}
                </button>

                <p className="hq-invite-submit-note">
                  Ao ativar a conta, confirma que é o destinatário autorizado deste convite.
                </p>
              </section>
            </form>
          </>
        ) : (
          <section
            className="hq-invite-state"
            role="alert"
          >
            <div className="hq-invite-state-icon hq-invite-state-icon--error">
              !
            </div>

            <div className="hq-invite-state-label hq-invite-state-label--error">
              Convite indisponível
            </div>

            <h1 className="hq-invite-state-title">
              Não foi possível utilizar este link
            </h1>

            <p className="hq-invite-state-text">
              {error ||
                "Este convite é inválido, já expirou ou já foi utilizado."}
            </p>

            <p className="hq-invite-state-note">
              Peça à LOADEvo ou ao administrador responsável o envio de um novo convite.
            </p>

            <Link
              className="hq-invite-state-button"
              to="/"
            >
              Voltar ao início de sessão
            </Link>
          </section>
        )}
      </main>
    </div>
  );
}